import React, { useState, useEffect } from 'react';
import { Shield, Smartphone, Lock, X, Check, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

export default function TotpSetup({ onClose }) {
  const [status, setStatus] = useState('loading'); // loading | setup | confirm | enabled
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [confirmCode, setConfirmCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const res = await api.get('/auth/totp/status');
      setStatus(res.data.enabled ? 'enabled' : 'setup');
    } catch (e) {
      setStatus('setup');
    }
  };

  const handleSetup = async () => {
    setIsLoading(true);
    try {
      const res = await api.post('/auth/totp/setup');
      setQrCode(res.data.qrCode);
      setSecret(res.data.secret);
      setStatus('confirm');
    } catch (e) {
      toast.error(e.response?.data?.error || 'שגיאה ביצירת QR');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!confirmCode || confirmCode.length !== 6) {
      toast.error('נא להזין קוד בן 6 ספרות');
      return;
    }
    setIsLoading(true);
    try {
      await api.post('/auth/totp/confirm', { secret, code: confirmCode });
      toast.success('אימות דו-שלבי הופעל בהצלחה');
      setStatus('enabled');
    } catch (e) {
      toast.error(e.response?.data?.error || 'קוד שגוי');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisable = async () => {
    if (!disablePassword) {
      toast.error('נא להזין סיסמה');
      return;
    }
    setIsLoading(true);
    try {
      await api.post('/auth/totp/disable', { password: disablePassword });
      toast.success('אימות TOTP הושבת');
      setStatus('setup');
      setDisablePassword('');
    } catch (e) {
      toast.error(e.response?.data?.error || 'שגיאה');
    } finally {
      setIsLoading(false);
    }
  };

  if (status === 'loading') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl p-8">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-6 h-6 text-green-600" />
            אימות דו-שלבי (TOTP)
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Setup mode — not enabled yet */}
        {status === 'setup' && (
          <div className="space-y-4 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <Smartphone className="w-8 h-8 text-green-600" />
            </div>
            <p className="text-gray-600">
              הוסף שכבת אבטחה נוספת. בסיסמא + קוד מיישומון אימות (Google Authenticator, Authy וכו').
            </p>
            <button
              onClick={handleSetup}
              disabled={isLoading}
              className="w-full py-3 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
            >
              {isLoading ? 'טוען...' : 'הפעל אימות דו-שלבי'}
            </button>
          </div>
        )}

        {/* Confirm mode — show QR */}
        {status === 'confirm' && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 text-center">
              סרוק את ה-QR Code ביישומון האימות שלך והזן את הקוד הראשון לאישור.
            </p>

            {qrCode && (
              <div className="flex justify-center">
                <img src={qrCode} alt="QR Code" className="w-48 h-48 border rounded-lg" />
              </div>
            )}

            <div className="bg-gray-50 p-3 rounded-lg text-center">
              <p className="text-xs text-gray-500 mb-1">אם לא ניתן לסרוק, הזן ידנית:</p>
              <code className="text-sm font-mono bg-white px-2 py-1 rounded border select-all">{secret}</code>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">קוד אימות ראשון</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={confirmCode}
                onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, ''))}
                className="w-full px-3 py-2 border rounded-lg text-center tracking-widest text-lg font-mono"
                placeholder="000000"
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleConfirm}
                disabled={isLoading}
                className="flex-1 py-2 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
              >
                {isLoading ? 'מאמת...' : <><Check className="w-4 h-4 inline ml-1" /> אישור</>}
              </button>
              <button
                onClick={() => setStatus('setup')}
                className="py-2 px-4 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                ביטול
              </button>
            </div>
          </div>
        )}

        {/* Enabled mode */}
        {status === 'enabled' && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
              <Check className="w-6 h-6 text-green-600" />
              <div>
                <p className="font-medium text-green-800">אימות דו-שלבי פעיל</p>
                <p className="text-sm text-green-700">נדרש קוד מיישומון האימות בכניסה</p>
              </div>
            </div>

            <div className="border-t pt-4">
              <p className="text-sm text-gray-600 mb-3">לביטול אימות דו-שלבי:</p>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Lock className="absolute right-3 top-2.5 w-4 h-4 text-gray-400" />
                  <input
                    type="password"
                    value={disablePassword}
                    onChange={(e) => setDisablePassword(e.target.value)}
                    placeholder="הזן סיסמה"
                    className="w-full pr-9 pl-3 py-2 border rounded-lg"
                  />
                </div>
                <button
                  onClick={handleDisable}
                  disabled={isLoading}
                  className="py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium"
                >
                  {isLoading ? '...' : 'בטל'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
